// services/tokenService.ts
import axios from 'axios'
import { store } from '../store/store'
import { clearToken, setRefreshing, setToken } from '../store/slices/inmutable'
import { AuthenticationServices } from './AuthenticationServices'

/**
 * El refresco en vuelo, compartido por todo el que lo pida mientras dura.
 *
 * Es una promesa de módulo y no la bandera `isRefreshing` del store porque una
 * bandera dice que hay un refresco, pero no deja esperarlo: el que llegaba
 * durante la ventana se llevaba el token vencido que todavía estaba en el
 * store. Mientras las rutas eran anónimas eso no se veía; con `[Authorize]` en
 * GetFavoriteAndRecentDestinations es un 401 y la libreta del remitente se
 * queda vacía. La bandera se sigue publicando porque es estado visible.
 */
let refreshInFlight: Promise<string> | null = null

export const initializeToken = async (): Promise<string> => {
  // Al abrir la aplicación, App.tsx dispara seis catálogos en paralelo y la
  // libreta se pide al montar: un solo login para todos, no siete.
  if (refreshInFlight) {
    return refreshInFlight
  }

  refreshInFlight = (async () => {
    try {
      store.dispatch(setRefreshing(true))
      const token = await AuthenticationServices()
      // `AuthenticationServices` devuelve '' cuando el login falla, y '' pasa
      // un `typeof === 'string'`: sin esto se guardaría un token vacío como si
      // fuera bueno y todo lo demás daría 401 durante media hora.
      if (typeof token !== 'string' || token === '') {
        throw new Error('Invalid token received')
      }
      // Asume que el token expira en 30 minutos (1800 segundos)
      store.dispatch(setToken({ token, expiresIn: 1800 }))
      return token
    } catch (error) {
      store.dispatch(clearToken())
      throw error
    } finally {
      store.dispatch(setRefreshing(false))
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

export const getCurrentToken = async (): Promise<string> => {
  const state = store.getState()
  const { token, tokenExpiration } = state.inmutable

  // Si no hay token o está a punto de expirar (menos de 5 minutos)
  const isExpired = !tokenExpiration || tokenExpiration - Date.now() < 300000

  if (!!token && !isExpired) {
    return token
  }

  // `initializeToken` reparte el refresco en vuelo, así que esto nunca lanza
  // siete logins ni devuelve el token de ayer a los que llegan tarde.
  return initializeToken()
}

/**
 * La petición que PIDE el token no puede pedir el token.
 *
 * `AuthenticationServices` publica contra `/Account/Login`, y el guardia de
 * abajo sólo miraba la palabra "Authentication", que esa ruta no lleva: el
 * login entraba al interceptor, pedía el token, y esperaba al refresco que él
 * mismo tenía que resolver. Con la bandera `isRefreshing` no se notaba porque
 * el que llegaba durante el refresco se iba con el token vencido en vez de
 * esperar, que es justo el fallo que esto viene a arreglar.
 */
export const isAuthEndpoint = (url?: string): boolean =>
  !!url && (url.includes('Account/Login') || url.includes('Authentication'))

// Configuración de axios para usar el token
axios.interceptors.request.use(
  async (config) => {
    if (isAuthEndpoint(config.url)) {
      return config // No agregar token a las peticiones de autenticación
    }

    try {
      const token = await getCurrentToken()
      config.headers.Authorization = `Bearer ${token}`
    } catch (error) {
      console.error('Error getting token:', error)
      // Puedes manejar el error aquí o dejar que se propague
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)
